/**
 * Offline job packs: what is saved on the phone, and which actions can wait for a signal.
 *
 * Reads: job pack answers (My shifts, each pack, its docket, kits and photos lists, notices) are saved each time
 * they load; with no signal the saved copy is shown. When My shifts loads, the packs and documents for every shift
 * starting in the next 48 hours (or under way) are saved ahead, so they are there on site.
 *
 * Writes: the actions below are saved to the outbox when there's no signal (or the request times out) and sent
 * when the phone is back online. The saved packs are updated straight away so the screen shows what was done.
 */
import { store } from "./store";

export const OFFLINE_EVENT = "ops-offline-state";
const AHEAD_MS = 48 * 3600_000;
const KEEP_AFTER_END_MS = 12 * 3600_000;

/* ------------------------------------------------------------------ reads */

const CACHEABLE = [/^\/shifts$/, /^\/shifts\/[0-9a-f-]{36}$/, /^\/shifts\/[0-9a-f-]{36}\/(docket|kits|photos)$/, /^\/notices$/];
export const cacheable = (path: string) => CACHEABLE.some((r) => r.test(path));
export const isDocumentPath = (path: string) => /^\/shifts\/[0-9a-f-]{36}\/documents\/[0-9a-f-]{36}$/.test(path);

interface Saved<T> { data: T; savedAt: string }
let offlineSince: string | null = null;
/** When the screen is showing saved copies: the oldest saved time shown, or null when online. */
export const offlineState = () => offlineSince;
function setOffline(savedAt: string | null) {
  const next = savedAt === null ? null : offlineSince && offlineSince < savedAt ? offlineSince : savedAt;
  if (next === offlineSince) return;
  offlineSince = next;
  if (typeof window !== "undefined") window.dispatchEvent(new Event(OFFLINE_EVENT));
}
export const markOnline = () => setOffline(null);

export async function save(path: string, data: unknown) {
  await store().put("cache", path, { data, savedAt: new Date().toISOString() } satisfies Saved<unknown>);
}
export async function saved<T>(path: string): Promise<Saved<T> | undefined> {
  return store().get<Saved<T>>("cache", path);
}

/** Load from the server and save it; with no signal, the saved copy (or the error when there is none). */
export async function readThrough<T>(path: string, load: () => Promise<T>, isNetwork: (e: unknown) => boolean): Promise<T> {
  try {
    const data = await load();
    markOnline();
    if (cacheable(path)) void save(path, data).catch(() => undefined);
    return data;
  } catch (e) {
    if (!isNetwork(e) || !cacheable(path)) throw e;
    const s = await saved<T>(path).catch(() => undefined);
    if (!s) throw e;
    setOffline(s.savedAt);
    return s.data;
  }
}

/**
 * Saved documents are kept by document, not by shift: a job's documents belong to all its shifts, so a TGS saved
 * ahead for one shift opens from any other shift's pack on the same job.
 */
export const fileKey = (path: string) => {
  const m = /\/documents\/([0-9a-f-]{36})$/.exec(path);
  return m ? `doc:${m[1]}` : null;
};

export async function readFileThrough(path: string, load: () => Promise<Blob>, isNetwork: (e: unknown) => boolean): Promise<Blob> {
  const key = isDocumentPath(path) ? fileKey(path) : null;
  try {
    const blob = await load();
    if (key) void store().put("files", key, { blob, savedAt: new Date().toISOString() }).catch(() => undefined);
    return blob;
  } catch (e) {
    if (!isNetwork(e) || !key) throw e;
    const s = await store().get<{ blob: Blob; savedAt: string }>("files", key).catch(() => undefined);
    if (!s) throw e;
    setOffline(s.savedAt);
    return s.blob;
  }
}

/* ------------------------------------------------------------------ ahead of the shift */

interface ShiftLike { id: string; startsAt: string; endsAt: string }
interface PackLike { documents?: { id: string }[] }

/** Save the packs and documents for shifts in the next 48 hours, and forget ones long finished. */
export async function saveAhead(
  shifts: ShiftLike[],
  get: (path: string) => Promise<unknown>,
  getFile: (path: string) => Promise<Blob>,
  now = Date.now(),
): Promise<number> {
  const soon = shifts.filter((s) => Date.parse(s.startsAt) - now <= AHEAD_MS && Date.parse(s.endsAt) + KEEP_AFTER_END_MS >= now);
  let files = 0;
  for (const s of soon) {
    const pack = (await get(`/shifts/${s.id}`).catch(() => null)) as PackLike | null;
    await Promise.all([`/shifts/${s.id}/docket`, `/shifts/${s.id}/kits`, `/shifts/${s.id}/photos`].map((p) => get(p).catch(() => null)));
    for (const d of pack?.documents ?? []) {
      const path = `/shifts/${s.id}/documents/${d.id}`;
      if (await store().get("files", `doc:${d.id}`).catch(() => undefined)) continue;
      if (await getFile(path).then(() => true, () => false)) files++;
    }
  }
  await forget(new Set(shifts.map((s) => s.id)), now);
  return files;
}

/** Drop saved packs for shifts no longer listed, and documents no saved pack lists any more. */
async function forget(listed: Set<string>, _now: number) {
  for (const { key } of await store().all("cache")) {
    const m = /^\/shifts\/([0-9a-f-]{36})/.exec(key);
    if (m && !listed.has(m[1]!)) await store().del("cache", key);
  }
  const keep = new Set<string>();
  for (const { key, value } of await store().all<Saved<PackLike>>("cache")) {
    if (/^\/shifts\/[0-9a-f-]{36}$/.test(key)) for (const d of value.data?.documents ?? []) keep.add(`doc:${d.id}`);
  }
  for (const { key } of await store().all("files")) if (!keep.has(key)) await store().del("files", key);
}

/* ------------------------------------------------------------------ writes that can wait */

interface Queueable { method: string; re: RegExp; label: (m: RegExpExecArray, body: string | null) => string; patch?: (m: RegExpExecArray, body: string | null) => Promise<void> }
const ID = "([0-9a-f-]{36})";
const parse = (b: string | null) => {
  try {
    return b ? (JSON.parse(b) as Record<string, unknown>) : {};
  } catch {
    return {};
  }
};

async function patchPack(bookingId: string, fn: (pack: Record<string, unknown>) => void) {
  const path = `/shifts/${bookingId}`;
  const s = await saved<Record<string, unknown>>(path);
  if (!s) return;
  fn(s.data);
  await store().put("cache", path, s);
}

const QUEUEABLE: Queueable[] = [
  {
    method: "POST",
    re: new RegExp(`^/shifts/${ID}/documents/${ID}/ack$`),
    label: () => "Document signed",
    patch: (m, body) =>
      patchPack(m[1]!, (p) => {
        const doc = (p.documents as { id: string; ackKind: string | null; ack: unknown }[] | undefined)?.find((d) => d.id === m[2]);
        if (doc && doc.ackKind) doc.ack = { kind: doc.ackKind, signedName: (parse(body).signedName as string | undefined) ?? null, acknowledgedAt: new Date().toISOString() };
      }),
  },
  { method: "PUT", re: new RegExp(`^/shifts/${ID}/checklists/(start|end)$`), label: (m) => `${m[2] === "start" ? "Start" : "End"} of shift checklist (saved so far)` },
  {
    method: "POST",
    re: new RegExp(`^/shifts/${ID}/checklists/(start|end)/submit$`),
    label: (m) => `${m[2] === "start" ? "Start" : "End"} of shift checklist`,
    patch: (m) =>
      patchPack(m[1]!, (p) => {
        const list = (p.field as { checklists?: Record<string, { status: string; current: { submittedAt: string | null } | null }> } | null)?.checklists?.[m[2]!];
        if (list) {
          list.status = "complete";
          if (list.current) list.current.submittedAt = new Date().toISOString();
        }
      }),
  },
  { method: "POST", re: new RegExp(`^/shifts/${ID}/incidents$`), label: () => "Incident report" },
  { method: "POST", re: /^\/incidents\/(\{[^}]+\}|[0-9a-f-]{36})\/photos$/, label: () => "Incident photo" },
  { method: "POST", re: new RegExp(`^/shifts/${ID}/docket/send$`), label: () => "Docket sent to the client to sign" },
  {
    method: "POST",
    re: new RegExp(`^/shifts/${ID}/reply$`),
    label: (_m, body) => (parse(body).response === "declined" ? "Can't do the shift" : "Shift confirmed"),
    patch: (m, body) =>
      patchPack(m[1]!, (p) => {
        const me = (p.viewer as { crewMemberId: string | null } | undefined)?.crewMemberId;
        const mine = (p.crew as { crewMemberId: string; response?: string | null; declineReason?: string | null }[] | undefined)?.find((c) => c.crewMemberId === me);
        const b = parse(body);
        if (mine) Object.assign(mine, { response: b.response ?? null, declineReason: b.response === "declined" ? (b.reason ?? null) : null, respondedAt: new Date().toISOString() });
      }),
  },
  { method: "POST", re: new RegExp(`^/shifts/${ID}/photos$`), label: () => "Set-up photo" },
  { method: "POST", re: new RegExp(`^/shifts/${ID}/kits/${ID}/check$`), label: () => "Kit check" },
];

/** If this action can wait for a signal: its label, the shift it belongs to, and how to show it as done. */
export function queueable(method: string, path: string, body: string | null): { label: string; bookingId: string | null; patch: () => Promise<void> } | null {
  for (const q of QUEUEABLE) {
    if (q.method !== method) continue;
    const m = q.re.exec(path);
    if (!m) continue;
    const booking = /^\/shifts\/([0-9a-f-]{36})/.exec(path)?.[1] ?? null;
    return { label: q.label(m, body), bookingId: booking, patch: () => (q.patch ? q.patch(m, body) : Promise.resolve()) };
  }
  return null;
}
